import { useQuery } from '@tanstack/react-query';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { FilterBar, type FilterField } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { analyticsFilterPaged, filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { todayDateString } from '@/api/services/riskDashboard';
import type { AnalyticsFilterInput } from '@/api/types/analytics';

export interface AnalyticsFilterState {
  begDate: string;
  endDate: string;
  symbol: string | null;
  selectedMifidClassification: string | null;
  selectedExecutionVenue: string | null;
  group: string | null;
  groupText: string | null;
  groupProfile: string | null;
  connectorId: number | null;
  countryId: number | null;
  specificLogin: number | null;
  portfolioProfile: number | null;
  specificUniqueId: string | null;
  byUniqueId: boolean;
  live: boolean;
  showLP: boolean;
}

export function defaultAnalyticsFilters(): AnalyticsFilterState {
  const today = todayDateString();
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yesterday = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, '0')}-${String(y.getDate()).padStart(2, '0')}`;
  return {
    begDate: yesterday,
    endDate: today,
    symbol: null,
    selectedMifidClassification: null,
    selectedExecutionVenue: null,
    group: null,
    groupText: null,
    groupProfile: null,
    connectorId: null,
    countryId: null,
    specificLogin: null,
    portfolioProfile: null,
    specificUniqueId: null,
    byUniqueId: false,
    live: false,
    showLP: false,
  };
}

export function toAnalyticsInput(f: AnalyticsFilterState, portfolioGuid: string): AnalyticsFilterInput {
  return {
    begDate: f.begDate,
    endDate: f.endDate,
    live: f.live,
    showLP: f.showLP,
    byUniqueId: f.byUniqueId,
    symbol: f.symbol,
    countryId: f.countryId,
    connectorId: f.byUniqueId ? null : f.connectorId,
    group: f.group,
    groupText: f.groupText,
    groupProfile: f.groupProfile,
    selectedExecutionVenue: f.selectedExecutionVenue,
    selectedMifidClassification: f.selectedMifidClassification,
    specificLogin: f.byUniqueId ? null : f.specificLogin,
    specificUniqueId: f.byUniqueId ? f.specificUniqueId : null,
    portfolioProfile: f.byUniqueId ? null : f.portfolioProfile,
    portfolioGuid,
  };
}

interface Props {
  draft: AnalyticsFilterState;
  setDraft: React.Dispatch<React.SetStateAction<AnalyticsFilterState>>;
  onFetch: () => void;
  onExtract: () => void | Promise<unknown>;
  onModeToggle: (key: 'byUniqueId' | 'live' | 'showLP', value: boolean) => void;
  canExcel: boolean;
  fetching: boolean;
}

export function AnalyticsFilterBar({ draft, setDraft, onFetch, onExtract, onModeToggle, canExcel, fetching }: Props) {
  const mifid = useQuery({ queryKey: ['rd-filter', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: 5 * 60_000 });
  const venues = useQuery({ queryKey: ['rd-filter', 'venues'], queryFn: filterDataApi.getExecutionVenues, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getEnabledConnectors, staleTime: 5 * 60_000 });
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });
  const groupProfiles = useQuery({ queryKey: ['rd-filter', 'groupProfiles'], queryFn: filterDataApi.getGroupProfiles, staleTime: 5 * 60_000 });

  const today = todayDateString();
  const str = (v: string | number | null) => (v == null ? '' : String(v));
  const lock = draft.portfolioProfile != null ? 'pe-none opacity-50' : '';
  const uid = draft.byUniqueId;
  const lk = (node: React.ReactNode) => <div className={lock}>{node}</div>;

  const fields: (React.ReactNode | FilterField)[] = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Range</label>
          <DateRangePicker
            mode="range"

            maxDate={today}
            value={{ start: draft.begDate, end: draft.endDate }}
            onChange={(v) => {
              const end = v.end;
              const start = v.start > end ? end : v.start;
              setDraft((f) => ({ ...f, begDate: start, endDate: end, live: end === today }));
            }}
          />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <div key="symbol">
      <label className="form-label">Instrument</label>
      <Select2Async placeholder="All" value={str(draft.symbol) || null} fetch={filterDataPaged.instruments} onChange={(v) => setDraft((f) => ({ ...f, symbol: v || null }))} />
    </div>,
    lk(<Select2 label="Mifid Classification" allowClear placeholder="All" value={str(draft.selectedMifidClassification)} options={mifid.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, selectedMifidClassification: v || null }))} />),
    lk(<Select2 label="Execution Venue" allowClear placeholder="All" value={str(draft.selectedExecutionVenue)} options={venues.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, selectedExecutionVenue: v || null }))} />),
    lk(
      <>
        <label className="form-label">Group</label>
        <Select2Async placeholder="All" value={str(draft.group) || null} fetch={analyticsFilterPaged.groups} onChange={(v) => setDraft((f) => ({ ...f, group: v || null }))} />
      </>,
    ),
    lk(
      <>
        <label className="form-label">Group Text Filter</label>
        <input className="form-control" placeholder="S*, *abc*, !SPOT" value={draft.groupText ?? ''} onChange={(e) => setDraft((f) => ({ ...f, groupText: e.target.value || null }))} />
      </>,
    ),
    lk(<Select2 label="Group Profile" allowClear placeholder="All" value={str(draft.groupProfile)} options={groupProfiles.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, groupProfile: v || null }))} />),
    ...(!uid ? [lk(<Select2 label="Connector" allowClear placeholder="All" value={str(draft.connectorId)} options={connectors.data ?? []} onChange={(v) => setDraft((f) => ({ ...f, connectorId: v ? Number(v) : null }))} />)] : []),
    lk(
      <>
        <label className="form-label">Country</label>
        <Select2Async placeholder="All" value={str(draft.countryId) || null} fetch={analyticsFilterPaged.countries} onChange={(v) => setDraft((f) => ({ ...f, countryId: v ? Number(v) : null }))} />
      </>,
    ),
    ...(!uid
      ? [lk(
          <>
            <label className="form-label">Login</label>
            <input className="form-control" inputMode="numeric" value={draft.specificLogin ?? ''} onChange={(e) => setDraft((f) => ({ ...f, specificLogin: e.target.value ? Number(e.target.value) : null }))} />
          </>,
        )]
      : []),
    ...(!uid
      ? [<Select2
          key="pf"
          label="Portfolio Profile"
          allowClear
          placeholder="All"
          value={str(draft.portfolioProfile)}
          options={portfolios.data ?? []}
          onChange={(v) =>
            setDraft((f) =>
              v
                ? {
                    ...f,
                    portfolioProfile: Number(v),
                    // Portfolio is exclusive: clear the filters it overrides so they
                    // are neither shown as active nor sent in the request (legacy parity).
                    group: null,
                    groupText: null,
                    groupProfile: null,
                    selectedMifidClassification: null,
                    selectedExecutionVenue: null,
                    connectorId: null,
                    countryId: null,
                    specificLogin: null,
                    specificUniqueId: null,
                  }
                : { ...f, portfolioProfile: null },
            )
          }
        />]
      : [
          <div key="uid">
            <label className="form-label">Unique ID</label>
            <input className="form-control" value={draft.specificUniqueId ?? ''} onChange={(e) => setDraft((f) => ({ ...f, specificUniqueId: e.target.value || null }))} />
          </div>,
        ]),
  ];

  return (
    <FilterBar
      fields={fields}
      visibleCount={4}
      modes={
        <>
          <Switch label="By UID" on={draft.byUniqueId} onToggle={(c) => onModeToggle('byUniqueId', c)} />
          <Switch label="Live Data" on={draft.live} onToggle={(c) => onModeToggle('live', c)} />
          <Switch label="Show LP" on={draft.showLP} onToggle={(c) => onModeToggle('showLP', c)} />
        </>
      }
      actions={
        <>
          <button type="button" className="btn btn-primary btn-sm" onClick={onFetch} disabled={fetching}>
            {fetching ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}
            Fetch
          </button>
          {canExcel && (
            <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={onExtract}>
              Extract
            </AsyncButton>
          )}
        </>
      }
    />
  );
}

function Switch({ label, on, onToggle }: { label: string; on: boolean; onToggle: (c: boolean) => void }) {
  const id = `an-switch-${label.replace(/\s+/g, '')}`;
  return (
    <div className="form-check form-switch mb-0">
      <input className="form-check-input" type="checkbox" role="switch" id={id} checked={on} onChange={(e) => onToggle(e.target.checked)} />
      <label className="form-check-label small" htmlFor={id}>{label}</label>
    </div>
  );
}
